// ABOUTME: Global store for managing dialogs/drawers state.
// ABOUTME: Ensures only one dialog can be open at a time with optional data payload.

import { createGStore } from "create-gstore";
import { useState } from "react";

type DialogData = Record<string, unknown>;

type GlobalDialogsState<T extends object = DialogData> = {
  currentDialog: string | null;
  data: T | null;
  open: (dialogId: string, dialogData?: T) => void;
  close: () => void;
  isOpen: (dialogId: string) => boolean;
};

const internalStore = createGStore(() => {
  const [currentDialog, setCurrentDialog] = useState<string | null>(null);
  const [data, setData] = useState<DialogData | null>(null);

  const open = (dialogId: string, dialogData?: DialogData) => {
    setCurrentDialog(dialogId);
    if (dialogData) {
      setData(dialogData);
    }
  };

  const close = () => {
    setCurrentDialog(null);
    setData(null);
  };

  const isOpen = (dialogId: string): boolean => currentDialog === dialogId;

  return {
    currentDialog,
    data,
    open,
    close,
    isOpen,
  };
});

export function useGlobalDialogs<
  T extends object = DialogData,
>(): GlobalDialogsState<T> {
  return internalStore() as GlobalDialogsState<T>;
}

// Imperative API for use outside React components/hooks
export const globalDialogsApi = {
  open: (dialogId: string, dialogData?: DialogData) => {
    internalStore.getState().open(dialogId, dialogData);
  },
  close: () => {
    internalStore.getState().close();
  },
  isOpen: (dialogId: string): boolean => {
    return internalStore.getState().isOpen(dialogId);
  },
  getState: () => {
    const state = internalStore.getState();
    return {
      currentDialog: state.currentDialog,
      data: state.data,
    };
  },
};
